/**
 * ThemeSelector Component
 *
 * Allows players to select game board theme (ocean, arctic, swamp, lava).
 * Displays theme options as images with the selected theme highlighted.
 *
 * Used by:
 * - LobbyPhase
 *
 * Responsibilities:
 * - Display theme options as images
 * - Highlight selected theme with border
 * - Handle theme selection
 *
 * Dependencies: types/game.types.ts, utils/images.ts, utils/colors.ts
 */

import React from 'react';
import { IMAGES } from '../../../utils/images';
import { COLORS } from '../../../utils/colors';
import type { Theme } from '../../types/game.types';

export interface ThemeSelectorProps {
    /** Array of available themes */
    themes: Theme[];
    /** Currently selected theme */
    selectedTheme: Theme;
    /** Callback when theme is selected */
    onThemeSelect: (theme: Theme) => void;
}

/**
 * ThemeSelector Component
 */
export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
    themes,
    selectedTheme,
    onThemeSelect,
}) => {
    return (
        <div className="room_map_container">
            <p className="room_map_text">Choose Map</p>
            <div className="room_map_options">
                {themes.map((theme) => {
                    const isSelected = selectedTheme === theme;
                    const themeImage = IMAGES.backgrounds[theme as keyof typeof IMAGES.backgrounds];

                    return (
                        <img
                            key={theme}
                            src={themeImage}
                            className="room_map_option"
                            alt={`${theme} theme`}
                            onClick={() => onThemeSelect(theme)}
                            style={
                                isSelected
                                    ? {
                                        border: `solid 0.5vh ${COLORS.selected.ocean}`,
                                    }
                                    : undefined
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default ThemeSelector;
